import React from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { DollarSign, Loader2, Shield } from 'lucide-react';

/* ─── Admin → Referrals tab (stats, diagnose tool, per-referrer table) ─── */
export const AdminReferralsTab = ({
  referralStats, diagnoseUsername, setDiagnoseUsername, diagnosing, diagnoseResult,
  handleDiagnoseAffiliate, markingPaidId, handleMarkPaid,
}) => {
  return (
          <div className="lm-enter">
            <h2 className="text-2xl font-bold mb-6" data-testid="referrals-heading">Referral Program</h2>
            {referralStats && (
              <>
                {/* Top-line stat strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <Card className="p-4">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Total signups</p>
                    <p className="text-2xl font-bold tabular-nums">{referralStats.total_referrals}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Across all referrers</p>
                  </Card>
                  <Card className="p-4">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Free signups</p>
                    <p className="text-2xl font-bold tabular-nums">{referralStats.total_free ?? 0}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">No commission</p>
                  </Card>
                  <Card className="p-4">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Monthly Pro</p>
                    <p className="text-2xl font-bold tabular-nums text-emerald-600">{referralStats.total_monthly_active ?? 0}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">= €{(referralStats.total_monthly_recurring ?? 0).toFixed(2)}/mo</p>
                  </Card>
                  <Card className="p-4">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Yearly Pro</p>
                    <p className="text-2xl font-bold tabular-nums text-emerald-600">{referralStats.total_yearly_active ?? 0}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">= €{(referralStats.total_yearly_recurring ?? 0).toFixed(2)}/yr</p>
                  </Card>
                </div>
                <Card className="p-4 mb-6 border-amber-500/40 bg-amber-500/[0.04]">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-amber-500/15 text-amber-600 flex items-center justify-center">
                        <DollarSign className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Total you owe right now</p>
                        <p className="text-2xl font-bold tabular-nums text-amber-600" data-testid="total-pending-payout">
                          €{(referralStats.total_pending_payout ?? 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground max-w-xs text-right">
                      Sum of pending commissions across {referralStats.active_referrers ?? 0} referrer(s). Mark as paid below after each PayPal/SEPA transfer.
                    </p>
                  </div>
                </Card>

                {/* Affiliate Diagnostic (P0 — drill into a single affiliate) */}
                <Card className="p-4 mb-6 border-dashed">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <div>
                      <p className="font-semibold text-sm">Diagnose Affiliate</p>
                      <p className="text-xs text-muted-foreground">Why is @username not earning? Enter their handle to walk every referral + paid invoice and see the exact reason each commission was or wasn&apos;t created.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="text"
                      value={diagnoseUsername}
                      onChange={(e) => setDiagnoseUsername(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleDiagnoseAffiliate(); }}
                      placeholder="username (e.g. johndoe)"
                      className="flex-1 min-w-[200px] h-9 px-3 rounded-md border border-input bg-background text-sm"
                      data-testid="affiliate-diagnose-input"
                    />
                    <Button
                      size="sm"
                      disabled={diagnosing}
                      onClick={handleDiagnoseAffiliate}
                      data-testid="affiliate-diagnose-btn"
                    >
                      {diagnosing
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Checking…</>
                        : <><Shield className="w-4 h-4 mr-2" />Diagnose</>}
                    </Button>
                  </div>
                  {diagnoseResult && (
                    <div className="mt-4 space-y-3" data-testid="affiliate-diagnose-result">
                      {/* Eligibility */}
                      <div className={`rounded-lg p-3 text-sm ${diagnoseResult.eligibility?.earns_cash ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                        <p className="font-semibold mb-1">
                          @{diagnoseResult.referrer?.username} —{' '}
                          {diagnoseResult.eligibility?.earns_cash
                            ? 'ELIGIBLE to earn commissions'
                            : 'NOT eligible to earn commissions'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Plan: <b>{diagnoseResult.eligibility?.plan}</b> ({diagnoseResult.eligibility?.billing_cycle || 'no cycle'}) ·
                          PayPal: <b>{diagnoseResult.eligibility?.has_paypal ? diagnoseResult.eligibility.paypal_email : 'NOT SET'}</b>
                        </p>
                        {diagnoseResult.eligibility?.blockers?.length > 0 && (
                          <ul className="text-xs text-red-700 dark:text-red-300 mt-2 list-disc list-inside">
                            {diagnoseResult.eligibility.blockers.map((b, i) => <li key={i}>{b}</li>)}
                          </ul>
                        )}
                      </div>
                      {/* Summary numbers */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                        <div className="rounded-md bg-muted/30 p-2">
                          <p className="uppercase tracking-wider text-muted-foreground text-[10px]">Referrals</p>
                          <p className="text-lg font-bold tabular-nums">{diagnoseResult.summary?.referrals_total ?? 0}</p>
                        </div>
                        <div className="rounded-md bg-muted/30 p-2">
                          <p className="uppercase tracking-wider text-muted-foreground text-[10px]">Paying</p>
                          <p className="text-lg font-bold tabular-nums">{diagnoseResult.summary?.referrals_paid ?? 0}</p>
                        </div>
                        <div className="rounded-md bg-muted/30 p-2">
                          <p className="uppercase tracking-wider text-muted-foreground text-[10px]">Paid Invoices</p>
                          <p className="text-lg font-bold tabular-nums">{diagnoseResult.summary?.invoices_paid_total ?? 0}</p>
                        </div>
                        <div className="rounded-md bg-emerald-500/10 p-2">
                          <p className="uppercase tracking-wider text-emerald-700 dark:text-emerald-300 text-[10px]">✓ Recorded</p>
                          <p className="text-lg font-bold tabular-nums text-emerald-600">{diagnoseResult.summary?.commissions_recorded ?? 0}</p>
                        </div>
                        <div className="rounded-md bg-red-500/10 p-2">
                          <p className="uppercase tracking-wider text-red-700 dark:text-red-300 text-[10px]">✗ Missing</p>
                          <p className="text-lg font-bold tabular-nums text-red-600">{diagnoseResult.summary?.commissions_missing ?? 0}</p>
                        </div>
                      </div>
                      {/* Skip reasons */}
                      {diagnoseResult.summary?.skip_reasons && Object.keys(diagnoseResult.summary.skip_reasons).length > 0 && (
                        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                          <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1.5">Why commissions were skipped:</p>
                          <ul className="text-xs space-y-1">
                            {Object.entries(diagnoseResult.summary.skip_reasons).map(([reason, count]) => (
                              <li key={reason}><b>{count}×</b> — {reason}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {/* Per-referral breakdown */}
                      {diagnoseResult.diagnostics?.length > 0 && (
                        <details className="rounded-lg border bg-muted/10 p-3">
                          <summary className="text-xs font-semibold cursor-pointer">Per-referral breakdown ({diagnoseResult.diagnostics.length})</summary>
                          <div className="mt-2 space-y-2 max-h-80 overflow-y-auto">
                            {diagnoseResult.diagnostics.map((d, i) => (
                              <div key={i} className="rounded-md bg-background p-2 text-xs">
                                <p className="font-semibold">@{d.referred_username} <span className="text-muted-foreground font-normal">({d.referred_plan}/{d.referred_cycle || 'no cycle'})</span></p>
                                {d.is_self_referral && <Badge variant="destructive" className="text-[10px] mt-1">SELF-REFERRAL</Badge>}
                                {d.stripe_error && <p className="text-red-600 mt-1">Stripe error: {d.stripe_error}</p>}
                                {!d.invoices?.length && <p className="text-muted-foreground mt-1">No paid invoices yet</p>}
                                {d.invoices?.map((inv, j) => (
                                  <div key={j} className="ml-2 mt-1 border-l-2 pl-2" style={{ borderColor: inv.status === 'recorded' ? '#10b981' : '#ef4444' }}>
                                    <p className="font-mono text-[10px] text-muted-foreground">{inv.invoice_id}</p>
                                    <p>€{(inv.amount || 0).toFixed(2)} · {inv.plan} · <b className={inv.status === 'recorded' ? 'text-emerald-600' : 'text-red-600'}>{inv.status}</b></p>
                                    {inv.reason && <p className="text-red-600 text-[11px]">{inv.reason}</p>}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </Card>

                {/* Per-referrer table */}
                <div className="space-y-3">
                  {referralStats.referrers?.map((ref) => (
                    <Card key={ref.user_id} className="p-5" data-testid={`referrer-${ref.username}`}>
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        {/* LEFT — identity + breakdown */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold">@{ref.username}</p>
                            {ref.affiliate_blocked && <Badge variant="outline" className="text-red-500 border-red-500 text-[10px]">Blocked</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{ref.email}</p>

                          <div className="mt-3 grid grid-cols-3 gap-2 max-w-md">
                            <div className="rounded-lg bg-muted/30 px-3 py-2">
                              <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Free</p>
                              <p className="text-sm font-semibold tabular-nums" data-testid={`free-${ref.username}`}>{ref.free_count}</p>
                            </div>
                            <div className="rounded-lg bg-emerald-500/10 px-3 py-2">
                              <p className="text-[10px] uppercase text-emerald-600 tracking-wider">Monthly</p>
                              <p className="text-sm font-semibold tabular-nums text-emerald-700" data-testid={`monthly-${ref.username}`}>{ref.monthly_active}</p>
                              <p className="text-[10px] text-emerald-600/80">€{ref.monthly_recurring.toFixed(2)}/mo</p>
                            </div>
                            <div className="rounded-lg bg-violet-500/10 px-3 py-2">
                              <p className="text-[10px] uppercase text-violet-600 tracking-wider">Yearly</p>
                              <p className="text-sm font-semibold tabular-nums text-violet-700" data-testid={`yearly-${ref.username}`}>{ref.yearly_active}</p>
                              <p className="text-[10px] text-violet-600/80">€{ref.yearly_recurring.toFixed(2)}/yr</p>
                            </div>
                          </div>

                          {/* Payout method line */}
                          <div className="mt-3 text-xs text-muted-foreground">
                            {ref.payout_method === 'paypal' && ref.paypal_email ? (
                              <span>Pay via <strong className="text-foreground">PayPal</strong> → <span className="font-mono select-all">{ref.paypal_email}</span></span>
                            ) : ref.payout_method === 'bank' && ref.iban ? (
                              <span>Pay via <strong className="text-foreground">SEPA</strong> → <span className="font-mono select-all">{ref.iban}</span>{ref.account_holder && <> ({ref.account_holder})</>}</span>
                            ) : (
                              <Badge variant="outline" className="text-orange-500 border-orange-500">No payout details set</Badge>
                            )}
                          </div>
                        </div>

                        {/* RIGHT — owed + action */}
                        <div className="flex flex-col items-end gap-2 min-w-[180px]">
                          <div className="text-right">
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">You owe</p>
                            <p className="text-2xl font-bold tabular-nums text-amber-600" data-testid={`pending-${ref.username}`}>
                              €{ref.pending_payout.toFixed(2)}
                            </p>
                            <p className="text-[10px] text-muted-foreground">Paid out lifetime: €{ref.paid_out.toFixed(2)}</p>
                          </div>
                          {ref.pending_payout > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={markingPaidId === ref.user_id}
                              onClick={() => handleMarkPaid(ref.user_id, ref.pending_payout)}
                              data-testid={`mark-paid-${ref.username}`}
                            >
                              {markingPaidId === ref.user_id ? 'Marking...' : `Mark €${ref.pending_payout.toFixed(2)} paid`}
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                  {(!referralStats.referrers || referralStats.referrers.length === 0) && (
                    <div className="text-center py-12 text-muted-foreground">No referrals yet</div>
                  )}
                </div>
              </>
            )}
          </div>
  );
};
